import { useCallback, useEffect, useRef, useState } from "react";
import { MessagesSquare, Send, Users, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

function fmtTime(iso) {
    try {
        return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch {
        return "";
    }
}

// Jul-2026 (User-Wunsch) — Standort-Team-Chat: interner Gruppen-Chat je Standort,
// alle Mitarbeiter automatisch Mitglied, WhatsApp-Benachrichtigung bei Nachrichten.
export default function TeamChatPage() {
    const { user } = useAuth();
    const [overview, setOverview] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const bottomRef = useRef(null);
    const lastTsRef = useRef(null);

    const load = useCallback(async (initial = false) => {
        try {
            if (initial) {
                const [o, m] = await Promise.all([
                    api.get("/team-chat/overview"),
                    api.get("/team-chat/messages"),
                ]);
                setOverview(o.data);
                setMessages(m.data.items);
                lastTsRef.current = m.data.items.at(-1)?.created_at || null;
            } else {
                const { data } = await api.get("/team-chat/messages", { params: { after: lastTsRef.current || undefined } });
                if (data.items.length) {
                    setMessages((prev) => [...prev, ...data.items.filter((n) => !prev.some((p) => p.id === n.id))]);
                    lastTsRef.current = data.items.at(-1).created_at;
                }
            }
        } catch { /* Polling-Fehler still ignorieren */ }
    }, []);

    useEffect(() => {
        load(true);
        const t = setInterval(() => load(false), 8000);
        return () => clearInterval(t);
    }, [load]);

    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

    const send = async () => {
        const t = text.trim();
        if (!t || sending) return;
        setSending(true);
        try {
            const { data } = await api.post("/team-chat/messages", { text: t });
            setMessages((m) => [...m, data]);
            lastTsRef.current = data.created_at;
            setText("");
        } catch {
            toast.error("Nachricht konnte nicht gesendet werden — bitte erneut versuchen.");
        }
        setSending(false);
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-6" data-testid="team-chat-page">
            <div className="flex items-center gap-2 mb-1">
                <MessagesSquare className="w-6 h-6 text-[#111827]" />
                <h1 className="text-2xl font-black text-[#111827]">Standort-Team-Chat</h1>
            </div>
            <p className="text-sm text-gray-500 mb-4">
                Alle Mitarbeiter deines Standorts sind automatisch Mitglied.
                {overview && (overview.whatsapp_live
                    ? " Neue Nachrichten werden per WhatsApp angekündigt."
                    : " WhatsApp-Ankündigung ist vorbereitet (aktiv, sobald der WhatsApp-Versand freigeschaltet ist).")}
            </p>

            {overview && (
                <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="team-chat-members">
                    <Users className="w-4 h-4 text-gray-400" />
                    {overview.members.map((m) => (
                        <span key={m.id} className="inline-flex items-center gap-1 text-xs font-bold bg-gray-100 text-gray-700 rounded-full px-2.5 py-1" data-testid={`team-chat-member-${m.id}`}>
                            {m.name}
                            <span className="text-gray-400 font-medium">· {m.role_label}</span>
                            {m.has_phone && <Smartphone className="w-3 h-3 text-emerald-500" title="WhatsApp-Benachrichtigung möglich" />}
                        </span>
                    ))}
                </div>
            )}

            <div className="border-2 border-gray-200 rounded-sm bg-white flex flex-col" style={{ height: "55vh" }}>
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="team-chat-messages">
                    {messages.length === 0 && (
                        <div className="text-sm text-gray-400 text-center pt-10">Noch keine Nachrichten — schreib die erste!</div>
                    )}
                    {messages.map((m) => {
                        const own = m.sender_id === (overview?.me || user?.id);
                        return (
                            <div key={m.id} className={`flex ${own ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[78%] rounded-sm px-3 py-2 border ${own ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"}`}>
                                    {!own && <div className="text-[11px] font-black text-[#111827]">{m.sender_name}</div>}
                                    <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                                    <div className="text-[10px] text-gray-400 mt-0.5 text-right">{fmtTime(m.created_at)}</div>
                                </div>
                            </div>
                        );
                    })}
                    <div ref={bottomRef} />
                </div>
                <div className="border-t border-gray-200 p-3 flex items-end gap-2">
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                        rows={1}
                        placeholder="Nachricht an dein Team …"
                        className="flex-1 resize-none rounded-sm border-2 border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
                        data-testid="team-chat-input"
                    />
                    <button
                        onClick={send}
                        disabled={sending || !text.trim()}
                        className="h-10 px-4 bg-[#111827] text-[#FFCC00] rounded-sm font-black text-sm disabled:opacity-40 hover:bg-black transition-colors inline-flex items-center gap-1.5"
                        data-testid="team-chat-send"
                    >
                        <Send className="w-4 h-4" /> Senden
                    </button>
                </div>
            </div>
        </div>
    );
}
